import React, { useEffect } from 'react'
import { useGetCustomerSourceByCustomerSourceId } from '../../../../../api/dealership/customers/sources/useGetCustomerSourceByCustomerSourceId'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useModal } from '../../../../../components/useModal'
import { find } from 'ramda'
import {
  fetchDaysOptions,
  fetchFrequencyOptions,
  fetchTimeOptions,
} from '../../../../../lib/utils/time_frequency'
import { timezonesOptions } from '../../../../../lib/utils/timezones'
import { getImageURL } from '../../../../../lib/utils/getImageURL'
import TextInput from '../../../../../components/FormElements/TextInput'
import SelectInput from '../../../../../components/FormElements/SelectInput'
import { Button } from '@demandlocal/components'
import { useEditCustomerSourceByCustomerSourceId } from '../../../../../api/dealership/customers/sources/useEditCustomerSourceByCustomerSourceId'
import toast from 'react-hot-toast'

const EditFacebookLeadsSource = ({
  customer_source_id,
}: {
  customer_source_id: number
}): JSX.Element => {
  const { data: source, isLoading } =
    useGetCustomerSourceByCustomerSourceId(customer_source_id)
  const formContext = useForm({
    mode: 'onSubmit',
  })

  const { closeModal } = useModal()

  const {
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = formContext
  const formValues = watch()
  useEffect(() => {
    if (!isLoading) {
      const fetchFrequency = find(
        (option) =>
          option.value === source?.mongoData?.config?.schedule_data?.fetch_frequency,
        fetchFrequencyOptions,
      )
      const fetchTime = find(
        (option) => option.value === source?.mongoData?.config?.schedule_data?.fetch_time,
        fetchTimeOptions,
      )
      const fetchDay = find(
        (option) => option.value === source?.mongoData?.config?.schedule_data?.fetch_day,
        fetchDaysOptions,
      )
      const timezone = find(
        (option) =>
          option.value ===
          source?.mongoData?.config?.schedule_data?.fetch_timezone?.value,
        timezonesOptions,
      )
      reset({
        source_name: source?.customer_source,
        date_from: source?.mongoData?.config?.data?.date_from,
        fetch_frequency: fetchFrequency,
        fetch_day: fetchDay,
        fetch_time: fetchTime,
        fetch_timezone: timezone,
      })
    }
  }, [
    isLoading,
    source?.customer_source,
    reset,
    source?.mongoData?.config?.data?.date_from,
    source?.mongoData?.config?.schedule_data?.fetch_frequency,
    source?.mongoData?.config?.schedule_data?.fetch_time,
    source?.mongoData?.config?.schedule_data?.fetch_day,
    source?.mongoData?.config?.schedule_data?.fetch_timezone?.value,
  ])

  const { mutateAsync: editCustomerSource } = useEditCustomerSourceByCustomerSourceId()
  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Saving...')
    await editCustomerSource({
      data: {
        customer_source_id,
        customer_source: values.source_name,
        data_provider_id: 3,
        _id: source?.mongoData?._id,
        mongoData: {
          config: {
            ...source?.mongoData?.config,
            schedule_data: {
              ...source?.mongoData?.config?.schedule_data,
              fetch_frequency: values.fetch_frequency?.value,
              fetch_day: values.fetch_day?.value,
              fetch_timezone: values?.fetch_timezone,
            },
          },
        },
      },
    })
    toast.success('Saved!', { id: toastId })
    closeModal()
  }
  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col px-12 overflow-hidden gap-12'}>
          <div className={'flex items-center gap-4'}>
            <div className={'w-12 h-12 rounded-full flex'}>
              <img
                src={getImageURL(`images/vendors/facebook_ads_logo.png`)}
                className={'object-contain'}
              />
            </div>
            <div className={'font-bold text-blue-primary text-[28px]'}>
              Edit {source?.customer_source}
            </div>
          </div>
          <div>
            <div>
              <div className={'w-1/2'}>
                <TextInput
                  rules={{ required: 'This field is required' }}
                  inverted
                  placeholder={`Ex. Elead import`}
                  name={`source_name`}
                  label={'Name your source'}
                  info={'Give it a clear name so you remember it easily'}
                />
              </div>
            </div>
            <div className={'pb-10 border-b-2 flex justify-start items-center '}>
              <div className={'flex'}>
                <div className={'flex flex-row gap-5'}>
                  <div className={'grow-0'}>
                    <SelectInput
                      required={'This field is required'}
                      name={`fetch_frequency`}
                      label={'Fetch frequency'}
                      options={fetchFrequencyOptions}
                    />
                  </div>
                  <div className={'grow-0'}>
                    {formValues[`fetch_frequency`]?.value === 'weekly' && (
                      <SelectInput
                        required={'This field is required'}
                        name={`fetch_day`}
                        label={'Fetch day'}
                        options={[
                          { label: 'Monday', value: 'monday' },
                          { label: 'Tuesday', value: 'tuesday' },
                          { label: 'Wednesday', value: 'wednesday' },
                          { label: 'Thursday', value: 'thursday' },
                          { label: 'Friday', value: 'friday' },
                          { label: 'Saturday', value: 'saturday' },
                          { label: 'Sunday', value: 'sunday' },
                        ]}
                      />
                    )}
                  </div>
                  <div className={'grow-0'}>
                    {formValues[`fetch_frequency`] &&
                      formValues[`fetch_frequency`]?.value !== 'one_time' && (
                        <SelectInput
                          required={'This field is required'}
                          disabled={
                            formValues[`fetch_frequency`]?.value === 'one_time' ||
                            !formValues[`fetch_frequency`]
                          }
                          name={`fetch_time`}
                          label={'Fetch time'}
                          options={fetchTimeOptions}
                        />
                      )}
                  </div>
                  <div className={'grow-0'}>
                    {formValues[`fetch_frequency`] &&
                      formValues[`fetch_frequency`]?.value !== 'one_time' && (
                        <SelectInput
                          required={'This field is required'}
                          disabled={
                            formValues[`fetch_frequency`]?.value === 'one_time' ||
                            !formValues[`fetch_frequency`]
                          }
                          name={`fetch_timezone`}
                          label={'Timezone'}
                          options={timezonesOptions}
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={'h-20  px-12'}>
          <div className={'items-center flex justify-between border-t-2 py-4'}>
            <Button
              variant={'secondary'}
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault()
                closeModal()
              }}>
              Close
            </Button>
            <Button disabled={isSubmitting} type={'submit'}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default EditFacebookLeadsSource
